import { useEffect, useRef, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Skeleton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useFeedback from '../../hooks/useFeedback';

// Thumbnails of the screenshots stored with a feedback entry, with a click to
// see one full size. The images sit behind the authenticated API, so each one is
// fetched as a blob and shown through an object URL.
export default function FeedbackScreenshots({ feedbackId, screenshots, onDelete }) {
  const { getFeedbackScreenshotBlob } = useFeedback();
  const [urls, setUrls] = useState({});
  const [zoomed, setZoomed] = useState(null);
  const urlsRef = useRef({});
  const list = Array.isArray(screenshots) ? screenshots : [];
  const ids = list.map(item => item._id).join(',');

  useEffect(() => {
    if (!feedbackId || !ids) return undefined;
    let cancelled = false;
    const load = async () => {
      const missing = ids.split(',').filter(id => id && !urlsRef.current[id]);
      await Promise.all(
        missing.map(async screenshotId => {
          try {
            const blob = await getFeedbackScreenshotBlob({ feedbackId, screenshotId });
            if (cancelled || !blob) return;
            const url = URL.createObjectURL(blob);
            urlsRef.current[screenshotId] = url;
            setUrls(prev => ({ ...prev, [screenshotId]: url }));
          } catch (err) {
            // A single unreadable screenshot stays on its placeholder.
          }
        })
      );
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [feedbackId, ids, getFeedbackScreenshotBlob]);

  // All object URLs of one entry are released together.
  useEffect(
    () => () => {
      Object.values(urlsRef.current).forEach(url => URL.revokeObjectURL(url));
      urlsRef.current = {};
      setUrls({});
    },
    [feedbackId]
  );

  if (list.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {list.map(screenshot => {
        const url = urls[screenshot._id];
        return (
          <Box key={screenshot._id} sx={{ position: 'relative' }}>
            {url ? (
              <Box
                component='img'
                src={url}
                alt={screenshot.fileName || 'Screenshot'}
                onClick={() => setZoomed(screenshot)}
                sx={{
                  width: 112,
                  height: 80,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  cursor: 'zoom-in',
                  '&:hover': { opacity: 0.8 },
                }}
              />
            ) : (
              <Skeleton variant='rounded' width={112} height={80} />
            )}
            {onDelete && (
              <IconButton
                size='small'
                aria-label={`${screenshot.fileName || 'Screenshot'} entfernen`}
                onClick={() => onDelete(screenshot._id)}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'background.paper' },
                }}
              >
                <CloseIcon fontSize='inherit' />
              </IconButton>
            )}
          </Box>
        );
      })}

      <Dialog open={Boolean(zoomed)} onClose={() => setZoomed(null)} maxWidth='lg' fullWidth>
        <DialogTitle>{zoomed?.fileName || 'Screenshot'}</DialogTitle>
        <DialogContent>
          {zoomed && urls[zoomed._id] ? (
            <Box
              component='img'
              src={urls[zoomed._id]}
              alt={zoomed.fileName || 'Screenshot'}
              sx={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
